/**
 * Painel de Detecção de Tópicos
 * Exibe segmentação por tema com timeline visual
 */

import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat'
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import FlashOnIcon from '@mui/icons-material/FlashOn'
import RefreshIcon from '@mui/icons-material/Refresh'
import StarIcon from '@mui/icons-material/Star'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import { TopicDetection, Topic } from '../../../types/transcription'
import { getTopicDetection, runOnDemandAnalysis } from '../../../services/api'
import AnalysisOutdatedBanner from './AnalysisOutdatedBanner'
import { contextualErrors, FriendlyError } from '../../../services/errorHandler'
import AnalysisNotAvailableCard from './AnalysisNotAvailableCard'
import AnalysisSkippedCard from './AnalysisSkippedCard'
import AnalysisProcessingCard from './AnalysisProcessingCard'

type Props = {
  fileId: string
  /** Status atual desta análise (do polling) */
  analysisTypeStatus?: string
  /** Callback quando análise on-demand é iniciada */
  onAnalysisStarted?: () => void
  /** Navega para um segmento na aba Transcrição */
  onNavigateToSegment?: (segmentIndex: number) => void
  /** Flag indicando que segmentos foram editados apos esta analise */
  analysisOutdated?: boolean
}

function formatTime(time: string): string {
  // Se já está no formato HH:MM:SS, converte para M:SS ou H:MM:SS
  if (time.includes(':')) {
    const parts = time.split(':')
    if (parts.length === 3) {
      const hours = parseInt(parts[0])
      const mins = parseInt(parts[1])
      const secs = parseInt(parts[2])
      if (hours > 0) {
        return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
      }
      return `${mins}:${secs.toString().padStart(2, '0')}`
    }
    return time
  }
  // Fallback para segundos
  const seconds = parseFloat(time)
  if (isNaN(seconds)) return time
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  if (mins > 0) {
    return `${mins}m ${secs}s`
  }
  return `${secs}s`
}

function TopicCard({
  topic,
  isActive,
  onClick,
  onNavigateToSegment,
  compact = false
}: {
  topic: Topic
  isActive: boolean
  onClick: () => void
  onNavigateToSegment?: (idx: number) => void
  compact?: boolean
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: compact ? 1.5 : 2,
        border: compact ? '1px solid' : '2px solid',
        borderColor: isActive ? topic.color : '#e0e0e0',
        borderRadius: compact ? 1.5 : 2,
        cursor: 'pointer',
        transition: 'all 0.2s',
        bgcolor: isActive ? `${topic.color}10` : '#fff',
        '--monkee-bg': isActive ? `${topic.color}10` : '#fff',
        '#monkee-file-transcript-client &': { '--monkee-bg': isActive ? `${topic.color}10` : '#fff', backgroundColor: isActive ? `${topic.color}10 !important` : '#fff !important', background: isActive ? `${topic.color}10 !important` : '#fff !important' },
        '&:hover': {
          borderColor: topic.color,
          boxShadow: compact ? 1 : 2
        }
      }}
      onClick={onClick}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: compact ? 1 : 2 }}>
        {/* Indicador de cor */}
        <Box
          sx={{
            width: compact ? 4 : 8,
            height: '100%',
            minHeight: compact ? 50 : 60,
            bgcolor: topic.color,
            '#monkee-file-transcript-client &': { backgroundColor: `${topic.color} !important`, background: `${topic.color} !important` },
            borderRadius: 0.5
          }}
        />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: compact ? 0.5 : 1, mb: compact ? 0.5 : 1, flexWrap: 'wrap' }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                fontSize: compact ? '0.85rem' : '1rem',
                lineHeight: 1.2
              }}
            >
              {topic.name}
            </Typography>
            <Chip
              label={`${(topic.confidence * 100).toFixed(0)}%`}
              size="small"
              sx={{
                fontSize: compact ? '0.55rem' : '0.65rem',
                height: compact ? 18 : 20,
                bgcolor: topic.color,
                '#monkee-file-transcript-client &': { backgroundColor: `${topic.color} !important`, background: `${topic.color} !important` },
                color: '#fff'
              }}
            />
          </Box>

          {/* Descrição */}
          <Typography
            variant="body2"
            sx={{
              color: '#666',
              mb: compact ? 1 : 1.5,
              lineHeight: 1.4,
              fontSize: compact ? '0.75rem' : '0.875rem',
              display: compact ? '-webkit-box' : 'block',
              WebkitLineClamp: compact ? 2 : 'unset',
              WebkitBoxOrient: 'vertical',
              overflow: compact ? 'hidden' : 'visible'
            }}
          >
            {topic.description}
          </Typography>

          {/* Timestamps */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: compact ? 1 : 2, mb: compact ? 1 : 1.5, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccessTimeIcon sx={{ fontSize: compact ? 14 : 16, color: '#888' }} />
              <Typography variant="caption" sx={{ color: '#666', fontSize: compact ? '0.65rem' : '0.75rem' }}>
                {formatTime(topic.startTime)} - {formatTime(topic.endTime)}
              </Typography>
            </Box>
            <Chip
              label={formatDuration(topic.duration)}
              size="small"
              variant="outlined"
              sx={{ fontSize: compact ? '0.55rem' : '0.65rem', height: compact ? 18 : 20 }}
            />
            {!compact && (
              <Typography variant="caption" sx={{ color: '#888' }}>
                {topic.segments.length} segmentos
              </Typography>
            )}
          </Box>

          {/* Keywords */}
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {topic.keywords.slice(0, compact ? 3 : 5).map((kw, i) => (
              <Chip
                key={i}
                label={kw}
                size="small"
                variant="outlined"
                sx={{
                  fontSize: compact ? '0.55rem' : '0.65rem',
                  height: compact ? 18 : 22,
                  borderColor: topic.color,
                  color: topic.color
                }}
              />
            ))}
            {compact && topic.keywords.length > 3 && (
              <Typography variant="caption" sx={{ color: '#888', fontSize: '0.55rem', alignSelf: 'center' }}>
                +{topic.keywords.length - 3}
              </Typography>
            )}
          </Box>

          {/* Botão de navegação — aparece apenas quando o card está selecionado */}
          {isActive && onNavigateToSegment && topic.segments.length > 0 && (
            <Box sx={{ mt: compact ? 1 : 1.5, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                size="small"
                variant="outlined"
                endIcon={<ArrowForwardIcon sx={{ fontSize: compact ? 12 : 14 }} />}
                onClick={(e) => { e.stopPropagation(); onNavigateToSegment(topic.segments[0]) }}
                sx={{
                  fontSize: compact ? '0.6rem' : '0.7rem',
                  textTransform: 'none',
                  borderColor: topic.color,
                  color: topic.color,
                  py: compact ? 0.25 : 0.5,
                  '&:hover': { borderColor: topic.color, bgcolor: `${topic.color}10` }
                }}
              >
                {compact ? 'Ver trecho' : 'Ver na transcrição'}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  )
}

function TopicTimeline({
  topics,
  totalDuration,
  compact = false
}: {
  topics: Topic[]
  totalDuration: number
  compact?: boolean
}) {
  return (
    <Box sx={{ mb: compact ? 2 : 3 }}>
      <Typography variant="subtitle2" sx={{ mb: compact ? 1 : 1.5, fontWeight: 600, fontSize: compact ? '0.8rem' : '0.875rem' }}>
        {compact ? 'Timeline' : 'Timeline Visual'}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          height: compact ? 28 : 40,
          borderRadius: compact ? 1 : 2,
          overflow: 'hidden',
          border: '1px solid #e0e0e0'
        }}
      >
        {topics.map((topic) => {
          const widthPercent = (topic.duration / totalDuration) * 100
          const showLabel = compact ? widthPercent > 20 : widthPercent > 10
          return (
            <Tooltip
              key={topic.id}
              title={`${topic.name} (${formatDuration(topic.duration)})`}
              arrow
              disableHoverListener={compact}
            >
              <Box
                sx={{
                  width: `${widthPercent}%`,
                  bgcolor: topic.color,
                  '#monkee-file-transcript-client &': { backgroundColor: `${topic.color} !important`, background: `${topic.color} !important` },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'opacity 0.2s',
                  '&:hover': {
                    opacity: 0.8
                  }
                }}
              >
                {showLabel && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#fff',
                      fontWeight: 600,
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      px: compact ? 0.5 : 1,
                      fontSize: compact ? '0.55rem' : '0.75rem'
                    }}
                  >
                    {topic.name}
                  </Typography>
                )}
              </Box>
            </Tooltip>
          )
        })}
      </Box>

      {/* Escala de tempo */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
        <Typography variant="caption" sx={{ color: '#888', fontSize: compact ? '0.6rem' : '0.75rem' }}>0:00</Typography>
        <Typography variant="caption" sx={{ color: '#888', fontSize: compact ? '0.6rem' : '0.75rem' }}>
          {formatTime(totalDuration.toString())}
        </Typography>
      </Box>
    </Box>
  )
}

function TransitionCard({
  transition,
  compact = false
}: {
  transition: { fromTopic: string; toTopic: string; timestamp: string; transitionType: 'smooth' | 'abrupt' }
  compact?: boolean
}) {
  const isAbrupt = transition.transitionType === 'abrupt'

  return (
    <Box
      sx={{
        p: compact ? 1 : 1.5,
        bgcolor: isAbrupt ? '#fff3e0' : '#f5f5f5',
        '#monkee-file-transcript-client &': { backgroundColor: isAbrupt ? '#fff3e0 !important' : '#f5f5f5 !important', background: isAbrupt ? '#fff3e0 !important' : '#f5f5f5 !important' },
        borderRadius: 1,
        mb: compact ? 0.75 : 1,
        overflow: 'hidden',
      }}
    >
      {/* Linha 1: timestamp + badge */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
        <Typography variant="caption" sx={{ color: '#666', fontSize: compact ? '0.6rem' : '0.75rem', fontWeight: 500 }}>
          {formatTime(transition.timestamp)}
        </Typography>
        <Chip
          label={isAbrupt ? (compact ? '⚡ Abrupta' : 'Abrupta') : (compact ? '→ Suave' : 'Suave')}
          size="small"
          variant="outlined"
          sx={{
            fontSize: compact ? '0.5rem' : '0.6rem',
            height: compact ? 16 : 18,
            color: isAbrupt ? '#f57c00' : '#4caf50',
            borderColor: isAbrupt ? '#f57c00' : '#4caf50',
            flexShrink: 0,
          }}
        />
      </Box>

      {/* Linha 2: from → to */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
        <Chip
          label={transition.fromTopic}
          size="small"
          sx={{
            fontSize: compact ? '0.55rem' : '0.65rem',
            height: compact ? 18 : 24,
            flex: '1 1 0',
            minWidth: 0,
            '& .MuiChip-label': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
            }
          }}
        />

        {isAbrupt ? (
          <FlashOnIcon sx={{ color: '#f57c00', fontSize: compact ? 14 : 16, flexShrink: 0 }} />
        ) : (
          <ArrowRightAltIcon sx={{ color: '#888', fontSize: compact ? 14 : 16, flexShrink: 0 }} />
        )}

        <Chip
          label={transition.toTopic}
          size="small"
          sx={{
            fontSize: compact ? '0.55rem' : '0.65rem',
            height: compact ? 18 : 24,
            flex: '1 1 0',
            minWidth: 0,
            '& .MuiChip-label': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
            }
          }}
        />
      </Box>
    </Box>
  )
}

export default function TopicDetectionPanel({ fileId, analysisTypeStatus, onAnalysisStarted, onNavigateToSegment, analysisOutdated }: Props) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [detection, setDetection] = useState<TopicDetection | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<FriendlyError | null>(null)
  const [selectedTopic, setSelectedTopic] = useState<string | null>(null)

  const [reanalyzing, setReanalyzing] = useState(false)

  const handleReanalyze = async () => {
    setReanalyzing(true)
    try {
      await runOnDemandAnalysis(fileId, 'topics')
      onAnalysisStarted?.()
    } finally {
      setReanalyzing(false)
    }
  }

  const loadDetection = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await getTopicDetection(fileId)
      setDetection(data)
    } catch (e: any) {
      const friendlyError = contextualErrors.analysis(e)
      setError(friendlyError)
      console.error('Erro ao carregar tópicos:', e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (analysisTypeStatus === 'SKIPPED' || analysisTypeStatus === 'PROCESSING') return
    loadDetection()
  }, [fileId, analysisTypeStatus])

  // Nome do tópico selecionado — usado para filtrar transições
  const selectedTopicData = selectedTopic && detection
    ? detection.topics.find(t => t.id === selectedTopic) ?? null
    : null

  // Filtra transições que envolvem o tópico selecionado
  const filteredTransitions = selectedTopicData && detection
    ? detection.transitions.filter(
        t => t.fromTopic === selectedTopicData.name || t.toTopic === selectedTopicData.name
      )
    : detection?.transitions ?? []
  if (analysisTypeStatus === 'SKIPPED') {
    return (
      <AnalysisSkippedCard
        title="Detecção de Tópicos"
        analysisType="topics"
        fileId={fileId}
        onAnalysisStarted={onAnalysisStarted}
      />
    )
  }

  if (analysisTypeStatus === 'PROCESSING') {
    return <AnalysisProcessingCard title="Detecção de Tópicos" />
  }

  if (loading) {
    return (
      <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
        <CardContent sx={{ textAlign: 'center', py: { xs: 4, sm: 6 } }}>
          <CircularProgress size={isMobile ? 36 : 48} />
          <Typography sx={{ mt: 2, color: '#666', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            {isMobile ? 'Detectando...' : 'Detectando tópicos...'}
          </Typography>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card elevation={0} sx={{ border: '1px solid #e0e0e0', bgcolor: '#fafafa', '--monkee-bg': '#fafafa', '#monkee-file-transcript-client &': { '--monkee-bg': '#fafafa', backgroundColor: '#fafafa !important', background: '#fafafa !important' } }}>
        <CardContent sx={{ textAlign: 'center', py: { xs: 3, sm: 4 } }}>
          <Box sx={{ fontSize: { xs: 36, sm: 48 }, mb: { xs: 1.5, sm: 2 } }}>⚠️</Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            {error.title}
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', mb: 2, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            {error.message}
          </Typography>
          {error.canRetry && (
            <Button
              variant="outlined"
              onClick={loadDetection}
              startIcon={<RefreshIcon />}
              size={isMobile ? 'small' : 'medium'}
            >
              {isMobile ? 'Tentar' : 'Tentar novamente'}
            </Button>
          )}
        </CardContent>
      </Card>
    )
  }

  if (!detection) {
    return (
      <AnalysisNotAvailableCard 
        title="Detecção de Tópicos" 
        onRetry={loadDetection} 
      />
    )
  }

  const totalDuration = detection.topics.reduce((sum, t) => sum + t.duration, 0)
  const dominantTopicObj = detection.topics.find(t => t.name === detection.dominantTopic)

  return (
    <Box>
      {analysisOutdated && (
        <AnalysisOutdatedBanner
          analysisType="topics"
          onReanalyze={handleReanalyze}
          isReanalyzing={reanalyzing}
        />
      )}
      {/* Header com resumo */}
      <Card elevation={0} sx={{ border: '1px solid #e0e0e0', mb: { xs: 2, sm: 3 } }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '0.95rem', sm: '1.25rem' } }}>
              📑 {isMobile ? 'Tópicos' : 'Detecção de Tópicos'}
            </Typography>
            <Tooltip title="Atualizar análise" disableHoverListener={isMobile}>
              <IconButton onClick={loadDetection} size="small">
                <RefreshIcon sx={{ fontSize: { xs: 18, sm: 22 } }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Estatísticas rápidas */}
          <Grid container spacing={{ xs: 1, sm: 3 }} alignItems="stretch" sx={{ mb: { xs: 2, sm: 3 } }}>
            <Grid item xs={6} sm={3} sx={{ display: 'flex' }}>
              <Paper elevation={0} sx={{ p: { xs: 1.25, sm: 2 }, textAlign: 'center', bgcolor: '#f5f5f5', '--monkee-bg': '#f5f5f5', '#monkee-file-transcript-client &': { '--monkee-bg': '#f5f5f5', backgroundColor: '#f5f5f5 !important', background: '#f5f5f5 !important' }, borderRadius: { xs: 1.5, sm: 2 }, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976d2', fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                  {detection.topics.length}
                </Typography>
                <Typography variant="caption" sx={{ color: '#666', fontSize: { xs: '0.6rem', sm: '0.75rem' } }}>
                  {isMobile ? 'Tópicos' : 'Tópicos Detectados'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3} sx={{ display: 'flex' }}>
              <Paper elevation={0} sx={{ p: { xs: 1.25, sm: 2 }, textAlign: 'center', bgcolor: '#f5f5f5', '--monkee-bg': '#f5f5f5', '#monkee-file-transcript-client &': { '--monkee-bg': '#f5f5f5', backgroundColor: '#f5f5f5 !important', background: '#f5f5f5 !important' }, borderRadius: { xs: 1.5, sm: 2 }, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#388e3c', fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                  {detection.transitions.length}
                </Typography>
                <Typography variant="caption" sx={{ color: '#666', fontSize: { xs: '0.6rem', sm: '0.75rem' } }}>
                  Transições
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ display: 'flex' }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.25, sm: 2 },
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: dominantTopicObj ? `${dominantTopicObj.color}15` : '#f5f5f5',
                  '--monkee-bg': dominantTopicObj ? `${dominantTopicObj.color}15` : '#f5f5f5',
                  '#monkee-file-transcript-client &': { '--monkee-bg': dominantTopicObj ? `${dominantTopicObj.color}15` : '#f5f5f5', backgroundColor: dominantTopicObj ? `${dominantTopicObj.color}15 !important` : '#f5f5f5 !important', background: dominantTopicObj ? `${dominantTopicObj.color}15 !important` : '#f5f5f5 !important' },
                  borderRadius: { xs: 1.5, sm: 2 },
                  border: dominantTopicObj ? (isMobile ? `1px solid ${dominantTopicObj.color}` : `2px solid ${dominantTopicObj.color}`) : undefined
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1 }, width: '100%' }}>
                  <StarIcon sx={{ color: dominantTopicObj?.color || '#ffc107', fontSize: { xs: 20, sm: 24 } }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" sx={{ color: '#666', display: 'block', fontSize: { xs: '0.6rem', sm: '0.75rem' } }}>
                      {isMobile ? 'Dominante' : 'Tópico Dominante'}
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: '0.85rem', sm: '1rem' },
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {detection.dominantTopic}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* Timeline */}
          <TopicTimeline topics={detection.topics} totalDuration={totalDuration} compact={isMobile} />

          {/* Distribuição */}
          <Typography variant="subtitle2" sx={{ mb: { xs: 1, sm: 1.5 }, fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
            {isMobile ? 'Distribuição' : 'Distribuição de Tempo'}
          </Typography>
          <Grid container spacing={{ xs: 0.5, sm: 1 }}>
            {detection.topicDistribution.map((dist) => {
              const topic = detection.topics.find(t => t.id === dist.topicId)
              if (!topic) return null
              return (
                <Grid item xs={12} key={dist.topicId}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
                    <Typography
                      variant="body2"
                      sx={{
                        minWidth: { xs: 80, sm: 180 },
                        fontWeight: 500,
                        fontSize: { xs: '0.7rem', sm: '0.875rem' },
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {topic.name}
                    </Typography>
                    <Box sx={{ flex: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={dist.percentage}
                        sx={{
                          height: { xs: 8, sm: 12 },
                          borderRadius: { xs: 4, sm: 6 },
                          bgcolor: '#f0f0f0',
                          '#monkee-file-transcript-client &': { backgroundColor: '#f0f0f0 !important', background: '#f0f0f0 !important' },
                          '& .MuiLinearProgress-bar': {
                            bgcolor: topic.color,
                            '#monkee-file-transcript-client &': { backgroundColor: `${topic.color} !important`, background: `${topic.color} !important` },
                            borderRadius: { xs: 4, sm: 6 }
                          }
                        }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, minWidth: { xs: 36, sm: 50 }, textAlign: 'right', fontSize: { xs: '0.65rem', sm: '0.875rem' } }}>
                      {dist.percentage.toFixed(isMobile ? 0 : 1)}%
                    </Typography>
                  </Box>
                </Grid>
              )
            })}
          </Grid>
        </CardContent>
      </Card>

      {/* Lista de Tópicos */}
      <Grid container spacing={{ xs: 2, sm: 3 }}>
        <Grid item xs={12} md={8}>
          <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
              <Typography variant="subtitle2" sx={{ mb: { xs: 1.5, sm: 2 }, fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                {isMobile ? 'Tópicos' : 'Tópicos em Ordem Cronológica'}
              </Typography>

              <Stack spacing={{ xs: 1.5, sm: 2 }}>
                {detection.topics.map((topic) => (
                  <TopicCard
                    key={topic.id}
                    topic={topic}
                    isActive={selectedTopic === topic.id}
                    onClick={() => setSelectedTopic(
                      selectedTopic === topic.id ? null : topic.id
                    )}
                    onNavigateToSegment={onNavigateToSegment}
                    compact={isMobile}
                  />
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Transições */}
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
              <Typography variant="subtitle2" sx={{ mb: { xs: 1, sm: 2 }, fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                {isMobile ? 'Transições' : 'Transições de Tópico'}
              </Typography>

              {/* Indicador de filtro ativo */}
              {selectedTopicData && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, p: 0.75, bgcolor: `${selectedTopicData.color}12`, '#monkee-file-transcript-client &': { backgroundColor: `${selectedTopicData.color}12 !important`, background: `${selectedTopicData.color}12 !important` }, borderRadius: 1, border: `1px solid ${selectedTopicData.color}40` }}>
                  <FilterAltIcon sx={{ fontSize: 14, color: selectedTopicData.color, flexShrink: 0 }} />
                  <Typography variant="caption" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#555', fontSize: isMobile ? '0.6rem' : '0.7rem' }}>
                    {selectedTopicData.name}
                  </Typography>
                  <Chip
                    label="Limpar"
                    size="small"
                    onClick={() => setSelectedTopic(null)}
                    sx={{ fontSize: '0.55rem', height: 16, cursor: 'pointer', flexShrink: 0 }}
                  />
                </Box>
              )}

              {filteredTransitions.map((transition, index) => (
                <TransitionCard key={index} transition={transition} compact={isMobile} />
              ))}

              {filteredTransitions.length === 0 && (
                <Typography sx={{ textAlign: 'center', color: '#666', py: { xs: 1.5, sm: 2 }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  {selectedTopicData
                    ? (isMobile ? 'Sem transições' : `Nenhuma transição para "${selectedTopicData.name}"`)
                    : (isMobile ? 'Apenas 1 tópico' : 'Apenas um tópico detectado')
                  }
                </Typography>
              )}

              <Box sx={{ mt: { xs: 1.5, sm: 2 }, p: { xs: 1, sm: 1.5 }, bgcolor: '#f5f5f5', '#monkee-file-transcript-client &': { backgroundColor: '#f5f5f5 !important', background: '#f5f5f5 !important' }, borderRadius: 1 }}>
                <Typography variant="caption" sx={{ color: '#666', fontSize: { xs: '0.6rem', sm: '0.75rem' }, lineHeight: 1.6 }}>
                  {isMobile ? (
                    <>
                      <strong>Legenda:</strong> → Suave | ⚡ Abrupta
                    </>
                  ) : (
                    <>
                      <strong>Legenda:</strong><br />
                      <ArrowRightAltIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} /> Transição suave<br />
                      <FlashOnIcon sx={{ fontSize: 14, verticalAlign: 'middle', color: '#f57c00' }} /> Transição abrupta
                    </>
                  )}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dica */}
      <Box sx={{ mt: { xs: 1.5, sm: 2 }, p: { xs: 1.5, sm: 2 }, bgcolor: '#f5f5f5', '#monkee-file-transcript-client &': { backgroundColor: '#f5f5f5 !important', background: '#f5f5f5 !important' }, borderRadius: 2 }}>
        <Typography variant="caption" sx={{ color: '#666', fontSize: { xs: '0.65rem', sm: '0.75rem' }, lineHeight: 1.5 }}>
          {isMobile ? (
            <>
              💡 Toque em um tópico para filtrar as transições e navegar para a transcrição. ⚡ indica mudança abrupta de assunto.
            </>
          ) : (
            <>
              💡 <strong>Dica:</strong> A timeline visual mostra a proporção de tempo dedicada a cada tópico.
              Clique em um tópico para filtrar as transições relacionadas{onNavigateToSegment ? ' e navegar direto ao trecho na aba Transcrição' : ''}. Transições abruptas podem indicar mudanças
              bruscas de assunto durante a conversa.
            </>
          )}
        </Typography>
      </Box>
    </Box>
  )
}
